import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import { TextInput, Button, Text, Switch, useTheme, HelperText, Snackbar, ActivityIndicator } from 'react-native-paper';
import { createUser, updateUser, resetUserPassword } from '../../../api/adminAPI';

const UserFormScreen = ({ route, navigation }) => {
  const theme = useTheme();
  const { user } = route.params || {};
  const isEditMode = !!user;
  
  // Form state
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isActive, setIsActive] = useState(true);

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  // Şifre güncelleme için ek alanlar
  const [newPassword, setNewPassword] = useState('');
  const [newConfirmPassword, setNewConfirmPassword] = useState('');
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');
  
  // Düzenleme modunda form alanlarını doldur
  useEffect(() => {
    if (isEditMode && user) {
      setUsername(user.username || '');
      setName(user.name || '');
      setSurname(user.surname || '');
      setEmail(user.email || '');
      setPhoneNumber(user.phone_number || '');
      setIsAdmin(user.role === 'admin');
      setIsActive(user.is_active === undefined ? true : !!user.is_active);
    }
  }, [isEditMode, user]);
  
  // Form doğrulama
  const validateForm = () => {
    const newErrors = {};
    
    if (!username.trim()) {
      newErrors.username = 'Kullanıcı adı gereklidir';
    }
    
    if (!email.trim()) {
      newErrors.email = 'E-posta adresi gereklidir';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
    }
    
    if (!isEditMode) {
      if (!password) {
        newErrors.password = 'Şifre gereklidir';
      } else if (password.length < 6) {
        newErrors.password = 'Şifre en az 6 karakter olmalıdır';
      }
      
      if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Şifreler eşleşmiyor';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Kullanıcı kaydetme/güncelleme işlemi
  const handleSave = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const userData = {
        username,
        name,
        surname,
        email,
        phone_number: phoneNumber,
        role: isAdmin ? 'admin' : 'user',
        is_active: isActive
      };
      
      // Şifre sadece yeni kullanıcı oluşturulurken veya düzenleme sırasında değiştirilmek istendiğinde eklenir
      if (!isEditMode || password) {
        userData.password = password;
      }
      
      let response;
      
      if (isEditMode) {
        response = await updateUser(user.id, userData);
      } else {
        response = await createUser(userData);
      }
      
      if (response.success) {
        setSnackbarMessage(isEditMode ? 'Kullanıcı güncellendi' : 'Yeni kullanıcı oluşturuldu');
        setSnackbarVisible(true);
        
        // Kısa bir süre bekleyip listeye dön
        setTimeout(() => {
          navigation.goBack();
        }, 1500);
      } else {
        setSnackbarMessage(response.message || 'Bir hata oluştu');
        setSnackbarVisible(true);
      }
    } catch (error) {
      console.error('Error saving user:', error);
      setSnackbarMessage(error.message || 'Kullanıcı kaydedilirken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.formContainer}>
        {isEditMode ? (
          <View style={{marginBottom: 20}}>
            <Text style={{fontWeight: 'bold', marginBottom: 4, marginTop: 16, color: 'rgb(125, 76, 236)'}}>Şifresini Değiştir</Text>
            <TextInput
              label="Yeni Şifre"
              value={newPassword}
              onChangeText={setNewPassword}
              secureTextEntry
              mode="outlined"
              style={styles.input}
              error={!!passwordChangeError}
              disabled={passwordChangeLoading}
            />
            <TextInput
              label="Yeni Şifre Tekrar"
              value={newConfirmPassword}
              onChangeText={setNewConfirmPassword}
              secureTextEntry
              mode="outlined"
              style={styles.input}
              error={!!passwordChangeError}
              disabled={passwordChangeLoading}
            />
            {passwordChangeError ? (
              <HelperText type="error">{passwordChangeError}</HelperText>
            ) : null}
            <Button
              mode="contained"
              onPress={async () => {
                setPasswordChangeError('');
                if (!newPassword || !newConfirmPassword) {
                  setPasswordChangeError('Şifre alanları boş olamaz');
                  return;
                }
                if (newPassword.length < 6) {
                  setPasswordChangeError('Şifre en az 6 karakter olmalı');
                  return;
                }
                if (newPassword !== newConfirmPassword) {
                  setPasswordChangeError('Şifreler eşleşmiyor');
                  return;
                }
                setPasswordChangeLoading(true);
                try {
                  // Özel şifre sıfırlama endpoint'ini kullan
                  const response = await resetUserPassword(user.id, newPassword);
                  if (response.success) {
                    setSnackbarMessage('Şifre başarıyla güncellendi');
                    setSnackbarVisible(true);
                    setNewPassword('');
                    setNewConfirmPassword('');
                  } else {
                    setPasswordChangeError(response.message || 'Şifre güncellenemedi');
                  }
                } catch (err) {
                  setPasswordChangeError('Şifre güncellenirken hata oluştu');
                } finally {
                  setPasswordChangeLoading(false);
                }
              }}
              style={[styles.button, {backgroundColor: 'rgb(125, 76, 236)'}]}
              loading={passwordChangeLoading}
              disabled={passwordChangeLoading}
              color="#fff"
            >
              Şifreyi Güncelle
            </Button>
          </View>
        ) : null}

        {/* Kullanıcı Adı */}
        <TextInput
          label="Kullanıcı Adı *"
          value={username}
          onChangeText={setUsername}
          mode="outlined"
          style={styles.input}
          error={!!errors.username}
          disabled={loading || (isEditMode && username === 'admin')} // admin kullanıcıadı değiştirilemez
        />
        {errors.username && <HelperText type="error">{errors.username}</HelperText>}
        
        {/* İsim */}
        <TextInput
          label="İsim"
          value={name}
          onChangeText={setName}
          mode="outlined"
          style={styles.input}
          disabled={loading}
        />
        
        {/* Soyisim */}
        <TextInput
          label="Soyisim"
          value={surname}
          onChangeText={setSurname}
          mode="outlined"
          style={styles.input}
          disabled={loading}
        />
        
        {/* E-posta */}
        <TextInput
          label="E-posta *"
          value={email}
          onChangeText={setEmail}
          mode="outlined"
          style={styles.input}
          error={!!errors.email}
          keyboardType="email-address"
          disabled={loading}
        />
        {errors.email && <HelperText type="error">{errors.email}</HelperText>}
        
        {/* Telefon */}
        <TextInput
          label="Telefon Numarası"
          value={phoneNumber}
          onChangeText={setPhoneNumber}
          mode="outlined"
          style={styles.input}
          keyboardType="phone-pad"
          disabled={loading}
        />
        
        {/* Şifre - Sadece yeni kullanıcı veya şifre değiştirme için göster */}
        {(!isEditMode || (isEditMode && password)) && (
          <>
            <TextInput
              label={isEditMode ? "Yeni Şifre" : "Şifre *"}
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              mode="outlined"
              style={styles.input}
              error={!!errors.password}
              disabled={loading}
            />
            {errors.password && <HelperText type="error">{errors.password}</HelperText>}
            
            <TextInput
              label={isEditMode ? "Yeni Şifre Tekrar" : "Şifre Tekrar *"}
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry
              mode="outlined"
              style={styles.input}
              error={!!errors.confirmPassword}
              disabled={loading}
            />
            {errors.confirmPassword && <HelperText type="error">{errors.confirmPassword}</HelperText>}
          </>
        )}
        
        {/*
     
        <View style={styles.switchContainer}>
          <Text>Admin Yetkisi</Text>
          <Switch
            value={isAdmin}
            onValueChange={setIsAdmin}
            disabled={loading || (isEditMode && username === 'admin')} // admin rolü değiştirilemez
          />
        </View>
        */}
        
        {/* Aktif/Pasif Durumu */}
        <View style={styles.switchContainer}>
          <Text>Aktif</Text>
          <Switch
            value={isActive}
            onValueChange={setIsActive}
            disabled={loading || (isEditMode && username === 'admin')} // admin durumu değiştirilemez
            color="rgb(125, 76, 236)"
          />
        </View>
        
        {/* Kaydet Butonu */}
        <Button
          mode="contained"
          onPress={handleSave}
          style={[styles.button, {backgroundColor: 'rgb(125, 76, 236)'}]}
          loading={loading}
          disabled={loading}
          color="#fff"
        >
          {isEditMode ? 'Güncelle' : 'Kullanıcı Oluştur'}
        </Button>
        
        {/* İptal Butonu */}
        <Button
          mode="outlined"
          onPress={() => navigation.goBack()}
          style={styles.button}
          disabled={loading}
          color="rgb(125, 76, 236)"
        >
          İptal
        </Button>
      </View>
      
      {/* Snackbar */}
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={{backgroundColor: 'rgb(125, 76, 236)'}}
        theme={{colors: {surface: '#fff'}}}
      >
        {snackbarMessage}
      </Snackbar>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  formContainer: {
    padding: 16,
  },
  input: {
    marginBottom: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginVertical: 12,
    paddingHorizontal: 8,
  },
  button: {
    marginTop: 12,
  },
  divider: {
    marginVertical: 16,
  },
});

export default UserFormScreen;
